import { boundsOf, fitCamera, measureTrack, mercX, round, simplifyForStorage, type Coord, type LngLat, type TrackMeasure } from './geo';
import { DEFAULT_PRODUCT, normaliseProduct, type Product } from './products';
import { DEFAULT_STYLE, STYLES, styleFromLegacyName, type StyleId } from './styles';

export type LabelSide = 'right' | 'left' | 'top' | 'bottom' | 'center';
export type StatsMode = 'imperial' | 'metric' | 'custom';

export interface Route extends TrackMeasure {
  id: string;
  name: string;
  source: 'gpx' | 'strava' | 'sample' | 'legacy';
  /** The line that is drawn. May be simplified; distance, gain and profile are measured before that. */
  coords: Coord[];
  /** Seconds, when the source had timestamps. */
  movingTime: number | null;
}

export interface Label {
  id: string;
  lng: number;
  lat: number;
  text: string;
  /** Where the text sits relative to the point. */
  side: LabelSide;
  dot: boolean;
  /** Horizontal text nudge in poster px. Only v1 orders use it (their text sat 12.5px right of the point). */
  dx?: number;
}

/** Map view. `zoom` is for the poster at its reference height (900 CSS px), so it is resolution independent. */
export interface Camera {
  center: LngLat;
  zoom: number;
  bearing: number;
}

export interface Design {
  v: 2;
  id: string | null;
  routes: Route[];
  style: StyleId;
  line: { width: number; color: string | null; endpoints: boolean };
  /** null = keep the whole route fitted in view. */
  camera: Camera | null;
  text: {
    title: string;
    subtitle: string;
    statsMode: StatsMode;
    statsTime: boolean;
    /** Only used when statsMode is 'custom'. */
    stats: string;
    left: string;
    right: string;
  };
  elevation: boolean;
  labels: Label[];
  product: Product;
  /** Set when converted from a v1 order. */
  legacyOrderId: string | null;
}

export const LIMITS = { title: 38, subtitle: 60, stats: 70, footer: 48, label: 40, labels: 30, lineMin: 1.5, lineMax: 9 };

export const uid = () => Math.random().toString(36).slice(2, 10);

export const emptyDesign = (): Design => ({
  v: 2,
  id: null,
  routes: [],
  style: DEFAULT_STYLE,
  line: { width: 5, color: null, endpoints: false },
  camera: null,
  text: { title: '', subtitle: '', statsMode: 'metric', statsTime: false, stats: '', left: '', right: '' },
  elevation: true,
  labels: [],
  product: DEFAULT_PRODUCT,
  legacyOrderId: null,
});

const str = (v: unknown, max: number) => (typeof v === 'string' ? v.slice(0, max) : '');
const num = (v: unknown, fallback: number) => (typeof v === 'number' && Number.isFinite(v) ? v : fallback);
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
const isLngLat = (lng: unknown, lat: unknown) =>
  typeof lng === 'number' && typeof lat === 'number' && Math.abs(lng) <= 180 && Math.abs(lat) <= 90;

/**
 * Canonical coordinate precision (about 10cm, elevation to 10cm). Applied when a route is
 * imported as well as when a design is loaded, so the stats the customer sees in the editor
 * are computed from exactly the numbers that are saved and printed.
 */
export function cleanCoords(input: unknown): Coord[] {
  if (!Array.isArray(input)) return [];
  const out: Coord[] = [];
  for (const c of input) {
    if (!Array.isArray(c) || !isLngLat(c[0], c[1])) continue;
    const ele = typeof c[2] === 'number' && Number.isFinite(c[2]) ? round(c[2], 1) : null;
    out.push([round(c[0], 6), round(c[1], 6), ele]);
  }
  return out;
}

/** The measurements saved with a route, or fresh ones from its line if they are missing or malformed. */
function storedMeasure(r: any, coords: Coord[]): TrackMeasure {
  const ok = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v >= 0;
  if (!ok(r?.distance) || !ok(r?.gain)) return measureTrack(coords);
  const profile =
    Array.isArray(r.profile) && r.profile.length >= 2 && r.profile.length <= 2000 && r.profile.every((v: unknown) => typeof v === 'number' && Number.isFinite(v))
      ? (r.profile as number[])
      : null;
  return { distance: r.distance, gain: r.gain, profile };
}

/** Coerce untrusted JSON (API response, localStorage, URL) into a valid Design. */
export function sanitiseDesign(input: unknown): Design {
  const d = (input && typeof input === 'object' ? input : {}) as Record<string, any>;
  const base = emptyDesign();
  const routes: Route[] = (Array.isArray(d.routes) ? d.routes : [])
    .map((r: any): Route => {
      const coords = cleanCoords(r?.coords);
      return {
        id: str(r?.id, 24) || uid(),
        name: str(r?.name, 120) || 'Route',
        source: ['gpx', 'strava', 'sample', 'legacy'].includes(r?.source) ? r.source : 'gpx',
        coords,
        movingTime: typeof r?.movingTime === 'number' ? r.movingTime : null,
        ...storedMeasure(r, coords),
      };
    })
    .filter((r: Route) => r.coords.length > 1);
  const labels: Label[] = (Array.isArray(d.labels) ? d.labels : [])
    .filter((l: any) => isLngLat(l?.lng, l?.lat))
    .slice(0, LIMITS.labels)
    .map((l: any): Label => ({
      id: str(l.id, 24) || uid(),
      lng: l.lng,
      lat: l.lat,
      text: str(l.text, LIMITS.label),
      side: ['right', 'left', 'top', 'bottom', 'center'].includes(l.side) ? l.side : 'right',
      dot: l.dot !== false,
      ...(typeof l.dx === 'number' && Number.isFinite(l.dx) && l.dx !== 0 ? { dx: clamp(l.dx, -100, 100) } : {}),
    }));
  const cam = d.camera;
  const camera: Camera | null =
    cam && Array.isArray(cam.center) && isLngLat(cam.center[0], cam.center[1]) && Number.isFinite(cam.zoom)
      ? { center: [cam.center[0], cam.center[1]], zoom: clamp(cam.zoom, 0, 20), bearing: num(cam.bearing, 0) }
      : null;
  const color = typeof d.line?.color === 'string' && /^#[0-9a-f]{6}$/i.test(d.line.color) ? d.line.color : null;
  return {
    v: 2,
    id: typeof d.id === 'string' && d.id ? d.id : null,
    routes,
    style: STYLES.some((s) => s.id === d.style) ? d.style : base.style,
    line: {
      width: clamp(num(d.line?.width, base.line.width), LIMITS.lineMin, LIMITS.lineMax),
      color,
      endpoints: d.line?.endpoints === true,
    },
    camera,
    text: {
      title: str(d.text?.title, LIMITS.title),
      subtitle: str(d.text?.subtitle, LIMITS.subtitle),
      statsMode: ['imperial', 'metric', 'custom'].includes(d.text?.statsMode) ? d.text.statsMode : base.text.statsMode,
      statsTime: d.text?.statsTime === true,
      stats: str(d.text?.stats, LIMITS.stats),
      left: str(d.text?.left, LIMITS.footer),
      right: str(d.text?.right, LIMITS.footer),
    },
    elevation: d.elevation !== false,
    labels,
    product: normaliseProduct({ ...DEFAULT_PRODUCT, ...(d.product ?? {}) }),
    legacyOrderId: typeof d.legacyOrderId === 'string' ? d.legacyOrderId : null,
  };
}

// ---------------------------------------------------------------------------
// v1 orders
// ---------------------------------------------------------------------------

/** The Firestore `orders` document written by the v1 builder. */
export interface LegacyOrder {
  orderId: string;
  style?: string | null;
  title?: string | null;
  location?: string | null;
  customData?: string | null;
  date?: string | null;
  smallPrint?: string | null;
  size?: string | null;
  framing?: string | null;
  frameColor?: string | null;
  markers?: Array<{ lng: number; lat: number; text?: string }> | null;
  mapBounds?: number[] | null;
}

// v1 poster widths by size (the map viewport was 46px narrower and 674px tall).
const LEGACY_POSTER_W: Record<string, number> = { '8x12 (20x30cm)': 600, '12x16 (30x40cm)': 675, '16x20 (40x50cm)': 720 };
const LEGACY_MAP_H = 674;

function legacyLines(gpxArray: unknown): Array<{ name: string; coords: unknown }> {
  const items = Array.isArray(gpxArray) ? gpxArray : gpxArray ? [gpxArray] : [];
  // A bare coordinate array: [[lng, lat, ele], ...]
  if (items.length && Array.isArray(items[0]) && typeof items[0][0] === 'number') {
    return [{ name: 'Route', coords: items }];
  }
  const out: Array<{ name: string; coords: unknown }> = [];
  items.forEach((item: any, i) => {
    // v1 only ever drew features[0] of an upload; Strava imports were bare Features.
    const feature = item?.features?.[0] ?? (item?.geometry ? item : null);
    const geom = feature?.geometry;
    if (!geom) return;
    const name = feature.properties?.name || `Route ${i + 1}`;
    if (geom.type === 'MultiLineString') out.push({ name, coords: (geom.coordinates as unknown[][]).flat() });
    else out.push({ name, coords: geom.coordinates });
  });
  return out;
}

/**
 * Rebuild a v2 design from a v1 order and its `gpxArray.json`.
 * v1 never stored the map view the customer finished on, only the initial fitted bounds,
 * so the camera reproduces that default view (exact unless they panned or zoomed).
 */
export function fromLegacyOrder(order: LegacyOrder, gpxArray: unknown): Design {
  const routes: Route[] = legacyLines(gpxArray)
    .map((l) => {
      const full = cleanCoords(l.coords);
      return { id: uid(), name: l.name, source: 'legacy' as const, coords: simplifyForStorage(full), movingTime: null, ...measureTrack(full) };
    })
    .filter((r) => r.coords.length > 1);

  const product = normaliseProduct({
    ...DEFAULT_PRODUCT,
    ...(order.size ? { size: order.size } : {}),
    ...(order.framing ? { framing: order.framing } : {}),
    ...(order.frameColor ? { frameColour: order.frameColor } : {}),
  } as Product);

  const mapW = (LEGACY_POSTER_W[product.size] ?? 600) - 46;
  let camera: Camera | null = null;
  const mb = order.mapBounds;
  if (Array.isArray(mb) && mb.length === 4 && mb.every((v) => Number.isFinite(v)) && mb[2] > mb[0] && mb[3] > mb[1]) {
    // Saved viewport bounds: the centre is the bounds' Mercator centre, the zoom follows from its width.
    const fit = fitCamera(mb as [number, number, number, number], mapW, LEGACY_MAP_H, { top: 0, right: 0, bottom: 0, left: 0 }, 22);
    camera = { center: fit.center, zoom: Math.log2(mapW / (mercX(mb[2]) - mercX(mb[0]))), bearing: 0 };
  } else {
    const b = boundsOf(routes.map((r) => r.coords));
    if (b) camera = { ...fitCamera(b, mapW, LEGACY_MAP_H, { top: 20, right: 20, bottom: 20, left: 20 }, 22), bearing: 0 };
  }

  const labels: Label[] = (order.markers ?? [])
    .filter((m) => m && isLngLat(m.lng, m.lat) && typeof m.text === 'string' && m.text.trim() !== '')
    // v1 centred a box on the point with 25px of left padding, so the words sat 12.5px to the right.
    .map((m) => ({ id: uid(), lng: m.lng, lat: m.lat, text: m.text!.slice(0, LIMITS.label), side: 'center' as const, dot: false, dx: 12.5 }));

  return {
    ...emptyDesign(),
    routes,
    style: styleFromLegacyName(order.style),
    line: { width: 5, color: null, endpoints: false },
    camera,
    text: {
      title: order.title ?? '',
      subtitle: order.location ?? '',
      statsMode: 'custom',
      statsTime: false,
      stats: order.customData ?? '',
      left: order.date ?? '',
      right: order.smallPrint ?? '',
    },
    labels,
    product,
    legacyOrderId: order.orderId,
  };
}
